import React, {
  startTransition,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react"
import gsap from "gsap"
import { navigate } from "gatsby"
import { ScrollTrigger } from "gsap/all"
import Lottie from "react-lottie"
import styled from "styled-components"
import { useLocation } from "@reach/router"

import colors from "assets/styles/colors"

import {
  addDebouncedEventListener,
  documentReady,
  fixBrokenScroll,
  isBrowser,
} from "utils/functions"

import { LoaderContext, ScreenContext } from "./Providers"
import animationData from "assets/lotties/hourglass_whiteSolid.json"

const DIMENSIONS = {
  width: 25,
  height: 25,
}

const DEFAULT_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
}

type Square = {
  x: number
  y: number
  width: number
  height: number
}

const PageLoader: React.FC = () => {
  const { load, setFinished, setLastPage, canFinish } =
    useContext(LoaderContext)

  const loader = useRef<HTMLDivElement>(null)
  const lottieWrap = useRef<HTMLDivElement>(null)
  const { mobile } = useContext(ScreenContext)
  const [initialLoad, setInitialLoad] = useState(true)

  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null)
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null)
  const [grid, setGrid] = useState<Square[] | null>(null)
  const [drawInComplete, setDrawInComplete] = useState(false)

  const location = useLocation()

  const drawSquare = useCallback(
    (square: Square) => {
      if (ctx) {
        ctx.beginPath()
        ctx.fillStyle = colors.blue400
        ctx.fillRect(square.x, square.y, square.width, square.height)
      }
    },
    [ctx]
  )

  const generateGrid = useCallback(() => {
    const grid: Square[] = []

    const createSquare = (
      xProgress: number,
      yProgress: number,
      gridSoFar: Square[]
    ): Square[] => {
      if (yProgress >= window.innerHeight) {
        return grid
      } else if (xProgress >= window.innerWidth) {
        return createSquare(0, yProgress + DIMENSIONS.height, gridSoFar)
      } else {
        const square = {
          x: xProgress,
          y: yProgress,
          width: DIMENSIONS.width,
          height: DIMENSIONS.height,
        }

        grid.push(square)

        return createSquare(xProgress + DIMENSIONS.width, yProgress, gridSoFar)
      }
    }

    return createSquare(0, 0, grid)
  }, [drawSquare, initialLoad])

  const clearSquare = useCallback(
    (square: Square) => {
      if (ctx) {
        ctx.clearRect(square.x, square.y, square.width, square.height)
      }
    },
    [ctx]
  )

  const drawAllSquares = useCallback(
    (grid: Square[], callback: Function | null) => {
      const shuffled = gsap.utils.shuffle(grid)

      shuffled.forEach((square, index, arr) => {
        setTimeout(() => {
          drawSquare(square)

          if (index === arr.length - 1) {
            setDrawInComplete(true)

            if (callback) {
              callback()
            }
          }
        }, index * (mobile ? 0.5 : 0.05))
      })
    },
    [drawSquare, setDrawInComplete, mobile]
  )

  const clearAllSquares = useCallback(
    (grid: Square[], callback: Function) => {
      const shuffled = gsap.utils.shuffle(grid)

      shuffled.forEach((square, index, arr) => {
        setTimeout(() => {
          clearSquare(square)

          if (index === arr.length - 1) {
            setDrawInComplete(false)

            if (callback) {
              callback()
            }
          }
        }, index * (mobile ? 0.5 : 0.05))
      })
    },
    [clearSquare, setDrawInComplete]
  )

  const setCanvasSize = () => {
    if (canvasRef) {
      canvasRef.width = window.innerWidth
      canvasRef.height = window.innerHeight
    }
  }

  useEffect(() => {
    if (grid) {
      drawAllSquares(grid, null)
    }
  }, [grid])

  useEffect(() => {
    if (canvasRef && isBrowser()) {
      setCanvasSize()
      setCtx(canvasRef.getContext("2d"))

      window.addEventListener("resize", setCanvasSize)

      return () => {
        window.removeEventListener("resize", setCanvasSize)
      }
    }
  }, [canvasRef, setCtx, isBrowser])

  useEffect(() => {
    if (load && setLastPage && load !== location.pathname) {
      setLastPage(location.pathname)
    }
  }, [setLastPage, location, load])

  useEffect(() => {
    if (loader.current && ctx && isBrowser()) {
      if (!grid) {
        setGrid(generateGrid())
      }

      addDebouncedEventListener(
        window,
        "resize",
        () => {
          setGrid(generateGrid())
        },
        500
      )

      return () => {
        window.removeEventListener("resize", () => {})
      }
    }
  }, [setGrid, generateGrid, ctx, isBrowser, grid])

  useEffect(() => {
    if (loader.current && grid) {
      if (initialLoad && canFinish) {
        setTimeout(fixBrokenScroll, 0)
        setTimeout(fixBrokenScroll, 1)

        documentReady().then(() => {
          setTimeout(fixBrokenScroll, 0)
          setTimeout(fixBrokenScroll, 1)

          setTimeout(() => {
            startTransition(() => {
              gsap.to(lottieWrap.current, {
                opacity: 0,
                duration: 0.5,
              })

              gsap.set(canvasRef, {
                background: "unset",
              })

              clearAllSquares(grid, () => {
                gsap.set(loader.current, { display: "none" })

                setInitialLoad(false)

                window.locomotiveScroll.start()
                ScrollTrigger.refresh()

                setFinished && setFinished(true)
              })
            })
          }, 4000)
        })
      } else if (load) {
        startTransition(() => {
          gsap.set(loader.current, { display: "flex" })

          drawAllSquares(grid, () => {
            navigate(load)
          })
        })
      } else if (drawInComplete && canFinish) {
        setTimeout(fixBrokenScroll, 0)
        setTimeout(fixBrokenScroll, 1)

        documentReady().then(() => {
          setTimeout(fixBrokenScroll, 0)
          setTimeout(fixBrokenScroll, 1)

          clearAllSquares(grid, () => {
            gsap.set(loader.current, { display: "none" })

            setFinished && setFinished(true)

            window.locomotiveScroll.start()

            ScrollTrigger.refresh()
          })
        })
      }
    }
  }, [load, initialLoad, grid, drawInComplete, canFinish])

  return (
    <Wrapper ref={loader}>
      <LottieWrap ref={lottieWrap}>
        <Lottie options={DEFAULT_OPTIONS} height={"100%"} width={"100%"} />
      </LottieWrap>

      <Canvas ref={ref => setCanvasRef(ref)} />
    </Wrapper>
  )
}

export default PageLoader

const Wrapper = styled.div`
  position: fixed;
  z-index: 99999;
  border: none;
  overflow: hidden;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
`

const LottieWrap = styled.div`
  position: fixed;
  opacity: 1;
  z-index: 3;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const Canvas = styled.canvas`
  background: ${colors.blue400};
  width: 100%;
  height: 100%;
`
